import { NgTemplateOutlet } from "@angular/common";
import { ChangeDetectorRef, Component, DestroyRef, EventEmitter, Input, OnInit, Output } from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { TranslocoDirective, TranslocoService } from "@jsverse/transloco";
import { ConfirmationService } from "primeng/api";
import { ButtonModule } from "primeng/button";
import { DividerModule } from "primeng/divider";
import { ProgressSpinnerModule } from "primeng/progressspinner";
import { Tooltip } from "primeng/tooltip";
import { finalize, Observable, shareReplay } from "rxjs";
import {
  TransactionAttachmentFileInfo,
  TransactionEntryAttachment,
  TransactionFileType,
  TransactionFileUploadState,
} from "../../../../domain/transaction-entry";
import { UserDetails } from "../../../../domain/user";
import { ConnectDialogService } from "../../../../services/connect-dialog.service";
import { ToastService } from "../../../../services/toast.service";
import { TransactionService } from "../../../../services/transaction.service";
import { UserService } from "../../../../services/user.service";
import { FileSizePipe } from "../../../pipes/file-size.pipe";
import { RanDatePipe } from "../../../pipes/ran-date.pipe";
import { saveBlobAsFile } from "../../../save-blob";

@Component({
  selector: "app-transaction-attachment",
  templateUrl: "./transaction-attachment.component.html",
  styleUrl: "./transaction-attachment.component.scss",
  imports: [
    RanDatePipe,
    FileSizePipe,
    ProgressSpinnerModule,
    TranslocoDirective,
    ButtonModule,
    DividerModule,
    NgTemplateOutlet,
    Tooltip,
  ],
  host: {
    class: "flex",
  },
})
export class TransactionAttachmentComponent implements OnInit {
  public FileUploadStateEnum = TransactionFileUploadState;
  public FileTypeEnum = TransactionFileType;

  @Input()
  public transactionId!: number;

  @Input()
  public file!: TransactionAttachmentFileInfo;

  // Optional so non-delete-capable call sites can omit it and the trash button just stays hidden.
  @Input()
  public parentEntry: TransactionEntryAttachment | null = null;

  @Output()
  public deleted = new EventEmitter<TransactionAttachmentFileInfo>();

  public isDownloading = false;
  public isPreviewing = false;
  public isDeleting = false;
  public currentUser: UserDetails | null = null;

  private fileBlob$!: Observable<Blob>;

  constructor(
    public readonly transactionService: TransactionService,
    private readonly toastService: ToastService,
    private readonly translocoService: TranslocoService,
    private readonly dialogService: ConnectDialogService,
    private readonly confirmationService: ConfirmationService,
    private readonly userService: UserService,
    private readonly cdr: ChangeDetectorRef,
    private readonly destroyRef: DestroyRef,
  ) {}

  ngOnInit(): void {
    this.fileBlob$ = this.transactionService.downloadAttachment$(this.transactionId, this.file.id).pipe(
      takeUntilDestroyed(this.destroyRef),
      shareReplay(1),
      finalize(() => {
        this.hideProgressSpinners();
      }),
    );

    this.userService
      .getSelf$()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((user) => {
        this.currentUser = user;
      });
  }

  // True when the file was deleted by a user (audit metadata is available).
  public get isDeletedByUser(): boolean {
    return this.file.deletedAt !== null && this.file.deletedByUser !== null;
  }

  /**
   * Whether the trash button should be shown: the file must be in Success state and not deleted,
   * and the current user must either be internal or the uploader of the parent entry.
   * Mirrors the backend permission gate.
   */
  public get canDelete(): boolean {
    if (!this.currentUser || !this.parentEntry) {
      return false;
    }
    if (this.file.deletedAt) {
      return false;
    }
    if (this.file.fileUploadState !== TransactionFileUploadState.Success) {
      return false;
    }
    if (this.currentUser.isInternal) {
      return true;
    }
    return this.parentEntry.createdByUser.id === this.currentUser.id;
  }

  // True when the file should be rendered as unusable inside the active layouts:
  // failed upload or auto-deleted (e.g., expired) without user-deletion audit metadata.
  public isFailedUploadOrDeleted(createdAt: Date | string, fileUploadState: string): boolean {
    const fileCreatedAt = typeof createdAt === "string" ? new Date(createdAt) : createdAt;
    const fiveMinutesAgo = new Date(Date.now() - 5 * 60 * 1000);
    return (
      (fileCreatedAt < fiveMinutesAgo && fileUploadState === TransactionFileUploadState.Pending) ||
      fileUploadState === TransactionFileUploadState.Failed ||
      (fileUploadState === TransactionFileUploadState.Deleted && !this.isDeletedByUser)
    );
  }

  public downloadFile(): void {
    if (this.isDownloading) {
      return;
    }
    this.isDownloading = true;

    this.fileBlob$.subscribe({
      next: (blob) => {
        saveBlobAsFile(blob, this.file.fileName);
        this.hideProgressSpinners();
      },
      error: () => {
        this.toastService.error(
          this.translocoService.translate("app.transactionDetails.downloadError", { fileName: this.file.fileName }),
        );
        this.hideProgressSpinners();
      },
    });
  }

  public previewFile(): void {
    if (this.isPreviewing) {
      return;
    }
    this.isPreviewing = true;

    this.fileBlob$.subscribe({
      next: (blob) => {
        this.dialogService.displayPdfPreviewDialog(this.file.fileName, blob);
        this.hideProgressSpinners();
      },
      error: () => {
        this.toastService.error(
          this.translocoService.translate("app.transactionDetails.downloadError", { fileName: this.file.fileName }),
        );
        this.hideProgressSpinners();
      },
    });
  }

  public confirmDelete(): void {
    this.confirmationService.confirm({
      key: "delete-attachment-file-confirmation-dialog",
      header: this.translocoService.translate("app.transactionAttachment.deleteDialog.header"),
      // Passing only the file name. The message body is assembled inside the dialog's #message template
      message: this.file.fileName,
      icon: "pi pi-exclamation-triangle",
      rejectButtonProps: {
        label: this.translocoService.translate("app.common.cancel"),
        severity: "secondary",
        outlined: true,
      },
      acceptButtonProps: {
        label: this.translocoService.translate("app.common.delete"),
        severity: "danger",
      },
      accept: () => this.runDelete(),
    });
  }

  private runDelete(): void {
    this.isDeleting = true;
    this.cdr.detectChanges();

    this.transactionService
      .deleteAttachment$(this.transactionId, this.file.id)
      .pipe(
        finalize(() => {
          this.isDeleting = false;
          this.cdr.detectChanges();
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: () => {
          this.toastService.success(
            this.translocoService.translate("app.transactionAttachment.deleteDialog.successMessage"),
          );
          this.deleted.emit(this.file);
        },
        error: () => {
          this.toastService.error(
            this.translocoService.translate("app.transactionAttachment.deleteDialog.errorMessage"),
          );
        },
      });
  }

  // Just calling this in finalize() *should* be enough, but it seems like the loading state doesn't
  // always end if that's the only place we call it. Not clear why.
  private hideProgressSpinners(): void {
    this.isDownloading = false;
    this.isPreviewing = false;
    this.cdr.detectChanges();
  }
}
